// src/utils/variationPropagation.ts
// ─────────────────────────────────────────────────────────────────
// Phase 4: Variation/RFI → Canonical BOM propagation
//
// Business rules:
//   1. When a PM approves a VAR (or records a client-signed approval),
//      the VAR's affectedBomLines[] delta is applied directly to the
//      canonical BOM — ADD lines appear as new BOM items flagged
//      NOT_ORDERED / PENDING_APPROVAL, REMOVE lines are retired,
//      CHANGE_QTY lines are updated. A change history record is written.
//
//   2. An open/overdue RFI that references a specific BOM line
//      (via bomLineId) creates a procurement block — that BOM line
//      cannot be moved to ON_ORDER until the RFI is closed/resolved.
//      This prevents ordering materials whose specification is
//      genuinely in dispute.
//
//   3. Both propagation results are surfaced in the Drawings,
//      Procurement / Jobs / RFQ modules so the PM can act on them
//      from the page that shows the alert — not by manually checking
//      a separate module.
// ─────────────────────────────────────────────────────────────────

import {
  addBOMLine, updateBOMLine, getBOMLineById, getBOMForJob,
  type CanonicalBOMLine, type BOMProcurementStatus,
} from './canonicalBOM'
import { MOCK_RFIS, type RFI, type VariationRequest } from './mockDataDrawings'

// ── Types ─────────────────────────────────────────────────────────

export type VARBomDeltaAction = 'ADD' | 'REMOVE' | 'CHANGE_QTY' | 'CHANGE_SPEC'

export interface VARBomDelta {
  action: VARBomDeltaAction
  bomLineId?: string | null    // existing CanonicalBOMLine.id for REMOVE/CHANGE_*
  // For ADD — a partial CanonicalBOMLine (everything except id/createdAt/updatedAt)
  description?: string
  code?: string
  qty?: number
  unit?: string
  unitCost?: number
  specRef?: string
  complianceNote?: string
  discipline?: string
}

export interface VARPropagationResult {
  varId: string
  appliedAt: string
  appliedByName: string
  linesAdded: string[]          // new CanonicalBOMLine.id[]
  linesRemoved: string[]        // retired BOMLine.id[]
  linesChanged: string[]        // updated BOMLine.id[]
  rfqFlagsRaised: number        // lines added that need a new RFQ
  errors: string[]
}

export interface RFIProcurementBlock {
  rfiId: string
  rfiReference: string
  rfiTitle: string
  bomLineId: string
  bomLineDescription: string
  status: 'OPEN' | 'OVERDUE'
  raisedByName: string
  dueDate: string | null
}

const PROPAGATION_KEY = 'tp_var_propagation_history'

function canStore() { return typeof window !== 'undefined' }
function readHistory(): VARPropagationResult[] {
  if (!canStore()) return []
  try { const r = window.localStorage.getItem(PROPAGATION_KEY); return r ? JSON.parse(r) : [] } catch { return [] }
}
function writeHistory(h: VARPropagationResult[]) {
  if (canStore()) window.localStorage.setItem(PROPAGATION_KEY, JSON.stringify(h))
}

export function getPropagationHistory(): VARPropagationResult[] { return readHistory() }
export function getPropagationForVar(varId: string): VARPropagationResult | null {
  return readHistory().find(r => r.varId === varId) ?? null
}
export function isVARAlreadyApplied(varId: string): boolean {
  return !!getPropagationForVar(varId)
}

// ── Core propagation engine ───────────────────────────────────────

/**
 * Apply a VAR's BOM deltas to the canonical BOM.
 * Called when PM clicks "Approve and apply to BOM" on a submitted VAR.
 */
export function applyVARtoBOM(
  variation: VariationRequest & { affectedBomLines?: VARBomDelta[] },
  approvedByName: string,
): VARPropagationResult {
  const now = new Date().toISOString()
  const result: VARPropagationResult = {
    varId: variation.id, appliedAt: now, appliedByName: approvedByName,
    linesAdded: [], linesRemoved: [], linesChanged: [],
    rfqFlagsRaised: 0, errors: [],
  }

  const deltas: VARBomDelta[] = variation.affectedBomLines ?? []

  if (deltas.length === 0) {
    // No structured deltas — create a placeholder ADD line from the VAR estimate
    const newLine = addBOMLine({
      jobId: variation.jobId, jobNumber: variation.jobNumber,
      code: `VAR-${variation.reference}`,
      description: `[${variation.reference}] ${variation.title}`,
      materialType: 'BUILD_MATERIAL',
      qty: 1, unit: 'item',
      unitCost: variation.totalEstimatedCost, total: variation.totalEstimatedCost,
      source: 'VARIATION', sourceDocId: variation.linkedDocId ?? null,
      sourcePageRef: null, drawingTaskId: null,
      variationId: variation.id, pooledFromJobId: null,
      complianceFlag: 'PENDING',
      complianceNote: 'Added by variation — compliance check required before RFQ',
      specRef: null, complianceCheckedAt: null, complianceCheckedBy: null,
      complianceVerifiedForThisJob: false,
      procurementStatus: 'NOT_ORDERED', poNumber: null, rfqLineId: null,
      stockStatus: 'NOT_AVAILABLE', receivedQty: 0, grnIds: [],
      warehouseReturnable: false, returnedQty: 0, returnedAt: null,
      returnedByName: null, returnNote: null,
      discipline: 'Variation', whsFlags: [],
      createdByName: approvedByName,
    })
    result.linesAdded.push(newLine.id)
    result.rfqFlagsRaised++
  }

  deltas.forEach(delta => {
    try {
      switch (delta.action) {
        case 'ADD': {
          const newLine = addBOMLine({
            jobId: variation.jobId, jobNumber: variation.jobNumber,
            code: delta.code ?? `VAR-${variation.reference}-ADD`,
            description: delta.description ?? `[${variation.reference}] New scope`,
            materialType: 'BUILD_MATERIAL',
            qty: delta.qty ?? 1, unit: delta.unit ?? 'item',
            unitCost: delta.unitCost ?? 0, total: (delta.qty ?? 1) * (delta.unitCost ?? 0),
            source: 'VARIATION', sourceDocId: variation.linkedDocId ?? null,
            sourcePageRef: null, drawingTaskId: null,
            variationId: variation.id, pooledFromJobId: null,
            complianceFlag: 'PENDING',
            complianceNote: delta.complianceNote ?? 'Added by variation — compliance check required',
            specRef: delta.specRef ?? null, complianceCheckedAt: null, complianceCheckedBy: null,
            complianceVerifiedForThisJob: false,
            procurementStatus: 'NOT_ORDERED', poNumber: null, rfqLineId: null,
            stockStatus: 'NOT_AVAILABLE', receivedQty: 0, grnIds: [],
            warehouseReturnable: false, returnedQty: 0, returnedAt: null,
            returnedByName: null, returnNote: null,
            discipline: delta.discipline ?? 'Variation', whsFlags: [],
            createdByName: approvedByName,
          })
          result.linesAdded.push(newLine.id)
          result.rfqFlagsRaised++
          break
        }
        case 'REMOVE': {
          if (!delta.bomLineId) { result.errors.push('REMOVE delta missing bomLineId'); break }
          const updated = updateBOMLine(delta.bomLineId, {
            procurementStatus: 'NOT_ORDERED' as BOMProcurementStatus,
            stockStatus: 'NOT_AVAILABLE',
            complianceNote: `[REMOVED by ${variation.reference}] ${delta.description ?? ''}`.trim(),
            qty: 0, total: 0,
          })
          if (updated) result.linesRemoved.push(delta.bomLineId)
          else result.errors.push(`REMOVE: bomLine ${delta.bomLineId} not found`)
          break
        }
        case 'CHANGE_QTY': {
          if (!delta.bomLineId) { result.errors.push('CHANGE_QTY delta missing bomLineId'); break }
          const existing = getBOMLineById(delta.bomLineId)
          if (!existing) { result.errors.push(`CHANGE_QTY: bomLine ${delta.bomLineId} not found`); break }
          const newQty = delta.qty ?? existing.qty
          const updated = updateBOMLine(delta.bomLineId, {
            qty: newQty,
            total: newQty * existing.unitCost,
            complianceNote: `[Qty changed by ${variation.reference}] ${existing.complianceNote ?? ''}`.trim(),
          })
          if (updated) result.linesChanged.push(delta.bomLineId)
          break
        }
        case 'CHANGE_SPEC': {
          if (!delta.bomLineId) { result.errors.push('CHANGE_SPEC delta missing bomLineId'); break }
          const updated = updateBOMLine(delta.bomLineId, {
            specRef: delta.specRef ?? undefined,
            complianceFlag: 'NEEDS_REVIEW',
            complianceNote: `[Spec changed by ${variation.reference}] Re-verify compliance before ordering`,
            complianceVerifiedForThisJob: false,
            description: delta.description
              ? `${getBOMLineById(delta.bomLineId)?.description ?? ''} [VAR: ${delta.description}]`
              : undefined,
          })
          if (updated) result.linesChanged.push(delta.bomLineId)
          break
        }
      }
    } catch (e) {
      result.errors.push(`Error applying delta ${delta.action}: ${(e as Error).message}`)
    }
  })

  const history = readHistory()
  history.unshift(result)
  writeHistory(history.slice(0, 200)) // keep last 200
  return result
}

// ── RFI procurement block detector ───────────────────────────────

/**
 * Return all open/overdue RFIs that reference a specific BOM line.
 * These create procurement blocks — the BOM line cannot be ordered
 * until the RFI is resolved.
 */
export function getRFIProcurementBlocks(jobId: string): RFIProcurementBlock[] {
  const openRFIs = MOCK_RFIS.filter(r =>
    r.jobId === jobId && ['SENT', 'DRAFT', 'OVERDUE'].includes(r.status) &&
    (r as any).bomLineId
  )
  const bomLines = getBOMForJob(jobId)
  const blocks: RFIProcurementBlock[] = []

  openRFIs.forEach(rfi => {
    const bomLineId = (rfi as any).bomLineId as string
    const line = bomLines.find(l => l.id === bomLineId)
    if (!line) return
    blocks.push({
      rfiId: rfi.id, rfiReference: rfi.reference, rfiTitle: rfi.title,
      bomLineId, bomLineDescription: line.description,
      status: rfi.status === 'OVERDUE' ? 'OVERDUE' : 'OPEN',
      raisedByName: rfi.raisedByName, dueDate: rfi.dueDate,
    })
  })
  return blocks
}

/**
 * Check a single BOM line for any blocking RFIs.
 */
export function getBOMLineRFIBlocks(bomLineId: string, jobId: string): RFIProcurementBlock[] {
  return getRFIProcurementBlocks(jobId).filter(b => b.bomLineId === bomLineId)
}

/**
 * Check whether a BOM line is safe to raise an RFQ/PO for.
 * Returns null if clear, or a description of the block.
 */
export function getProcurementClearance(bomLineId: string, jobId: string): string | null {
  const blocks = getBOMLineRFIBlocks(bomLineId, jobId)
  if (blocks.length === 0) return null
  return `Blocked by ${blocks.map(b => `${b.rfiReference} (${b.status})`).join(', ')} — resolve RFI before ordering`
}

// ── VAR delta summary for UI ─────────────────────────────────────

export function summariseVARImpact(variation: VariationRequest & { affectedBomLines?: VARBomDelta[] }): {
  adds: number; removes: number; changes: number;
  estimatedMaterialDelta: number; needsRFQ: boolean
} {
  const deltas = variation.affectedBomLines ?? []
  const adds    = deltas.filter(d => d.action === 'ADD').length || (deltas.length === 0 ? 1 : 0)
  const removes = deltas.filter(d => d.action === 'REMOVE').length
  const changes = deltas.filter(d => ['CHANGE_QTY','CHANGE_SPEC'].includes(d.action)).length
  const materialDelta = deltas.filter(d => d.action === 'ADD').reduce((s, d) => s + ((d.qty ?? 1) * (d.unitCost ?? 0)), 0)
    || variation.estimatedMaterialCost
  return {
    adds, removes, changes,
    estimatedMaterialDelta: materialDelta,
    needsRFQ: adds > 0,
  }
}
